import Modal from '@organisms/Modal/Modal';
import Heading from '@atoms/Heading/Heading';
import { useTranslations } from 'next-intl';
import { clearCart } from '@api/interfaces/cartApi';
import { convertProductsToAnalyticsEcommerceItems } from '@helpers/analyticsHelpers/analyticsProduct';
import pushGTMEvent, { EcommerceEvent, pushGTMEcommerceEvent } from '@helpers/analyticsHelpers/pushGTMEvent';
import useCart from '@hooks/useCart';

interface Props {
  visible: boolean;
  setVisibility: (visibility: boolean) => void;
  tracking: { actionFieldList: string; category: string; action: string };
}
const EmptyCartModal = ({ visible, setVisibility, tracking }: Props) => {
  const { cart, refreshCart } = useCart();

  const closeEmptyCartModal = () => {
    setVisibility(false);
  };

  const trackEmptyCart = () => {
    const listName = tracking.actionFieldList;
    const products = cart?.products;
    pushGTMEcommerceEvent({
      event: EcommerceEvent.REMOVE_FROM_CART,
      ecommerce: {
        cart_id: cart?.code || '',
        items: convertProductsToAnalyticsEcommerceItems(products || [], undefined, listName),
      },
    });

    pushGTMEvent({
      event: 'Track',
      category: tracking.category,
      action: tracking.action,
      label: '',
      value: 0,
    });
  };

  const emptyCart = () =>
    new Promise((resolve, reject) => {
      const isReplacingOrder = Boolean(cart?.orderReference);
      clearCart(isReplacingOrder)
        .then((res) => refreshCart(res.data))
        .then(() => {
          closeEmptyCartModal();
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        })
        .then(() => trackEmptyCart())
        .then(() => {
          resolve('Successfully emptied cart');
        })
        .catch(() => {
          reject(new Error('Failed to clear cart'));
        });
    });

  const t = useTranslations('modal');

  if (!visible) return null;

  return (
    <Modal
      size="small"
      onClose={closeEmptyCartModal}
      onConfirm={emptyCart}
      closeOnEscape
      cancelButtonText={t('modal.cancel')}
      confirmButtonText={t('modal.cart.empty.confirm')}
      buttonWidth={100}
    >
      <Heading variant="h3">{t('modal.cart.empty.title')}</Heading>
    </Modal>
  );
};

export default EmptyCartModal;
